import React, { useState, useRef, useEffect } from 'react';
import LeftSidebar from '../../Tools/LeftSidebar';
import Topbar from '../../Tools/Topbar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import api from '../../../../api';

const Profile = () => {
  // State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    role_name: '',
    password: '',
  });
  const [roleId, setRoleId] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Hooks and refs
 const {settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Constants
  const ROLE_MAPPING = {
    1: 'Admin',
    2: 'Resource Manager',
    3: 'End User'
  };

  // Helper functions
  const getRoleName = (id) => ROLE_MAPPING[id] || 'Unknown';

  const getImageUrl = (url) => {
    if (!url) return '../assets/images/profile/profile-placeholder.png';
    if (url.startsWith('data:image') || url.startsWith('blob:')) return url;
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.jfif'];
    const hasValidExtension = validExtensions.some(ext => url.toLowerCase().endsWith(ext));
    return hasValidExtension ? url : `${url}.jpg`;
  };

  // Effects
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = sessionStorage.getItem('auth_token');
        const userId = sessionStorage.getItem('user_id');

        if (userId && token) {
          const response = await api.get('/users-management');
          const currentUser = response.data.data.find(user => user.id.toString() === userId);
          
          if (currentUser) {
            setUserData(currentUser);
            setFormData({
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              email: currentUser.email,
              phone: currentUser.phone,
              address: currentUser.address,
              role_name: getRoleName(currentUser.role_id),
              password: '',
            });
            setRoleId(currentUser.role_id || '');
            setImagePreview(getImageUrl(currentUser.pic));
          }
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
        setImagePreview('../assets/images/profile/profile-placeholder.png');
      }
    };

    fetchUserData();
  }, []);

  // Handlers
  const previewProfileImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setProfileImageFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userId = sessionStorage.getItem('user_id');
      
      // Handle image upload if new image selected
      let imageUrl = userData.pic;
      if (profileImageFile) {
        const formDataImage = new FormData();
        formDataImage.append('pic', profileImageFile);

        const imageResponse = await api.post(
          `/editable-user-info/${userId}`,
          formDataImage,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (imageResponse.data.status === 'success') {
          imageUrl = imageResponse.data.image_url || imageResponse.data.pic || imageResponse.data.data?.pic;
        }
      }

      // Prepare payload
      const payload = new FormData();
      payload.append('first_name', formData.first_name);
      payload.append('last_name', formData.last_name);
      payload.append('username', `${formData.first_name}${Math.floor(Math.random() * 1000)}`);
      payload.append('phone', formData.phone);
      payload.append('email', formData.email);
      payload.append('address', formData.address);
      payload.append('role_id', roleId);
      if (imageUrl) payload.append('pic', imageUrl);
      if (formData.password) payload.append('password', formData.password);

      // Submit form
      const response = await api.post(`/editable-user-info/${userId}`, payload);

      if (response.data.status === 'success') {
        const updatedUser = {
          ...userData,
          ...formData,
          pic: imageUrl,
          role_id: roleId
        };
        setUserData(updatedUser);
        setImagePreview(getImageUrl(imageUrl));
        setFormData(prev => ({ ...prev, password: '' }));
        setProfileImageFile(null);
        alert('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  if (!userData) {
  return (
    <div id="main-wrapper">
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <div className="card w-100 p-5 text-center">
              <h5>Loading profile...</h5>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

  return (
    <div id="main-wrapper">
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
          <Settings 
            ref={settingsRef}
            updateSetting={updateSetting}
            handleColorTheme={handleColorTheme}
            settings={settings}  // Add this line
          />
            
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Profile Overview</h5>
              </div>
              <div className="card-body p-4">
                <div className="py-3 px-0">
                  {/* Profile Overview Section */}
                  <div className="profile-first-panel">
                    <div className="profile-pic-wrapper" onClick={() => document.getElementById('profileUpload').click()}>
                      <img 
                        src={imagePreview} 
                        alt="Profile" 
                        className="img-fluid rounded-circle"
                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = '../assets/images/profile/profile-placeholder.png';
                        }}
                      />
                      <input
                        type="file"
                        id="profileUpload"
                        accept="image/jpeg, image/png, image/webp, image/jfif"
                        onChange={previewProfileImage}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="profile-details px-md-5">
                      <ProfileDetail label="Name:" value={`${userData.first_name} ${userData.last_name}`} />
                      <ProfileDetail label="Role:" value={getRoleName(userData.role_id)} />
                      <ProfileDetail label="Email:" value={userData.email} />
                      <ProfileDetail label="Last Login:" value={userData.last_login ? new Date(userData.last_login).toLocaleString() : 'N/A'} />
                    </div>
                  </div>

                  <hr className="my-5" />

                  {/* Profile Form Section */}
                  <div className="profile-edit-form">
                    <h5 className="card-title fw-semibold mb-4 lh-sm">Editable User Information Form</h5>
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <FormInput label="First Name" name="first_name" value={formData.first_name} onChange={handleInputChange} type="text" required />
                        <FormInput label="Last Name" name="last_name" value={formData.last_name} onChange={handleInputChange} type="text" required />
                        <FormInput label="Email Address" name="email" value={formData.email} onChange={handleInputChange} type="email" required />
                        <FormInput label="Phone Number" name="phone" value={formData.phone} onChange={handleInputChange} type="tel" />
                        <FormTextarea label="Address" name="address" value={formData.address} onChange={handleInputChange} rows={3} placeholder="Enter your full address" />
                        <FormInput label="Role" name="role_name" value={formData.role_name} onChange={handleInputChange} type="text" required readOnly />
                        <div className="col-md-12 mt-4">
                          <button type="submit" className="btn btn-primary px-4 py-2">Save</button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Components
const ProfileDetail = ({ label, value }) => (
  <div className="row mb-3">
    <div className="col-4"><h6 className="fw-semibold mb-0">{label}</h6></div>
    <div className="col-8"><p className="mb-0">{value || 'N/A'}</p></div>
  </div>
);

const FormInput = ({ label, name, value, onChange, type, required, readOnly, placeholder }) => (
  <div className="col-md-6">
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input
        type={type}
        className="form-control"
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
      />
    </div>
  </div>
);

const FormTextarea = ({ label, name, value, onChange, rows, placeholder }) => (
  <div className="col-md-12">
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <textarea
        className="form-control"
        rows={rows}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  </div>
);

export default Profile;